/**
 * @file useAppShell.js
 * @description App 根壳层：登录/注册弹窗、全局提示、Nav/Footer 显隐
 * @module shared/composables/useAppShell
 */

import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { goHomeAfterLogout } from '@/shared/utils/logoutNavigation'
import { useWxMpOAuthBootstrap } from '@/shared/composables/useWxMpOAuthBootstrap'
import { resumePendingTipAfterOAuth } from '@/features/comic/composables/useComicTip'
import { resumePendingGoldPayAfterOAuth } from '@/features/user/composables/useGoldRecharge'
import { peekPendingGoldPayResume } from '@/features/user/utils/goldPayPendingOAuth'
import { AUTH_CHANGED_EVENT, emitAuthChanged } from '@/shared/utils/authEvents'
import {
  consumeTipDeepLinkFromRoute,
  openPendingTipDeepLinkAfterLogin,
} from '@/features/comic/composables/useTipDeepLink'
import { peekPendingTipResume } from '@/features/comic/utils/tipPendingOAuth'
import { handleOAuthErrorFromRoute } from '@/shared/utils/wxMpOAuth'
import {
  clearAuthSession,
  getAuthToken,
  restoreAuthSession,
  saveAuthUser,
} from '@/shared/utils/auth'

export function useAppShell() {
  const router = useRouter()
  const route = useRoute()
  const userStore = useUserStore()
  const { bootstrapOAuthToken } = useWxMpOAuthBootstrap({
    onSessionRestored: (userData) => {
      user.value = userData
    },
  })

  const showLogin = ref(false)
  const showRegister = ref(false)
  const initialSession = restoreAuthSession()
  if (initialSession) {
    userStore.setUser(initialSession)
  }
  const user = ref(initialSession)
  const globalPopupMessage = ref('')
  const globalPopupType = ref('')
  const globalShowPopup = ref(false)
  const hideNavFooter = ref(false)

  function toggleLoginPopup() {
    showLogin.value = !showLogin.value
    showRegister.value = false
  }

  function toggleRegisterPopup() {
    showRegister.value = !showRegister.value
    showLogin.value = false
  }

  function switchToRegisterPopup() {
    showLogin.value = false
    showRegister.value = true
  }

  function switchToLoginPopup() {
    showRegister.value = false
    showLogin.value = true
  }

  function applyRestoredSession() {
    const session = restoreAuthSession()
    if (session) {
      userStore.setUser(session)
      user.value = session
      return true
    }
    userStore.clearUser()
    user.value = null
    return false
  }

  function handleRegisterSuccess(userData) {
    showRegister.value = false
    user.value = userData
    saveAuthUser(userData)
    userStore.setUser(userData)
    emitAuthChanged()
    openTipAfterAuth()
  }

  function handleLoginSuccess(userData) {
    showLogin.value = false
    user.value = userData
    saveAuthUser(userData)
    userStore.setUser(userData)
    emitAuthChanged()
    openTipAfterAuth()
  }

  function handleLogout() {
    userStore.clearUser()
    user.value = null
    clearAuthSession()
    emitAuthChanged()
    goHomeAfterLogout(router)
  }

  function showPopupMessage(message, type) {
    globalPopupMessage.value = message
    globalPopupType.value = type
    globalShowPopup.value = true
    setTimeout(() => {
      globalShowPopup.value = false
    }, 2000)
  }

  function toggleNavFooter(targetState) {
    if (targetState !== undefined) {
      hideNavFooter.value = targetState
    } else {
      hideNavFooter.value = !hideNavFooter.value
    }
  }

  function tryConsumeTipDeepLink() {
    const result = consumeTipDeepLinkFromRoute(route, router)
    if (result === 'need-login') {
      showPopupMessage('请先登录后再打赏', 'login-required')
      showLogin.value = true
    }
  }

  function openTipAfterAuth() {
    if (openPendingTipDeepLinkAfterLogin()) return
    tryConsumeTipDeepLink()
  }

  function onAuthChangedEvent() {
    applyRestoredSession()
  }

  onMounted(async () => {
    window.addEventListener(AUTH_CHANGED_EVENT, onAuthChangedEvent)
    const oauthConsumed = await bootstrapOAuthToken()
    const oauthError = oauthConsumed ? null : handleOAuthErrorFromRoute(router)

    if (oauthConsumed) {
      if (peekPendingTipResume()) {
        await resumePendingTipAfterOAuth()
      }
      if (peekPendingGoldPayResume()) {
        await resumePendingGoldPayAfterOAuth()
      }
    } else if (oauthError) {
      applyRestoredSession()

      const tokenBeforeDelay = getAuthToken()
      await new Promise((resolve) => setTimeout(resolve, 300))
      if (getAuthToken() && getAuthToken() !== tokenBeforeDelay) {
        console.info('[tip-pay] oauthError retry cancelled: token absorbed during delay')
      } else if (peekPendingTipResume() || peekPendingGoldPayResume()) {
        const oauthErrorMessages = {
          denied: '已取消微信授权',
          missing_code: '微信授权未完成，正在重试…',
          invalid_callback: '微信授权未完成，正在重试…',
        }
        showPopupMessage(oauthErrorMessages[oauthError] || '微信授权失败，请重试', 'error')
        if (peekPendingTipResume()) {
          await resumePendingTipAfterOAuth({
            fromOAuthError: oauthError,
            keepPending: true,
          })
        }
        if (peekPendingGoldPayResume()) {
          await resumePendingGoldPayAfterOAuth({
            fromOAuthError: oauthError,
            keepPending: true,
          })
        }
      }
    } else {
      applyRestoredSession()
    }
    if (route.query.login === '1') {
      showLogin.value = true
      const nextQuery = { ...route.query }
      delete nextQuery.login
      router.replace({ query: nextQuery })
    }

    tryConsumeTipDeepLink()
  })

  onBeforeUnmount(() => {
    window.removeEventListener(AUTH_CHANGED_EVENT, onAuthChangedEvent)
  })

  watch(
    () => route.fullPath,
    () => {
      tryConsumeTipDeepLink()
    }
  )

  return {
    showLogin,
    showRegister,
    user,
    globalPopupMessage,
    globalPopupType,
    globalShowPopup,
    hideNavFooter,
    toggleLoginPopup,
    toggleRegisterPopup,
    switchToRegisterPopup,
    switchToLoginPopup,
    handleRegisterSuccess,
    handleLoginSuccess,
    handleLogout,
    showPopupMessage,
    toggleNavFooter,
  }
}
